import Link from "next/link";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import { useViewStack } from "@dionatanbig2be/big2be-ui";
import { Icon } from "@/shared/components/Icon";
import { MediaImage } from "@/shared/components/MediaImage";
import type { CategoryItem } from "../../types/category-panel";
import { lastOf, resolveCategoryPath } from "../../lib/resolve-category-path";
import { CategoryPanelProducts } from "./CategoryPanelProducts";
import { CategoryPanelVideo } from "./CategoryPanelVideo";

export interface CategoryDetailCardProps {
    category: CategoryItem;
    onClose?: () => void;
    className?: string;
}

/** Mesma tipografia para subcategorias e marcas — um pouco maior que o menu lateral. */
const ITEM_CLASS = "text-base text-base-content/70 transition-colors hover:text-primary";

/**
 * Nome da subcategoria com o ícone escolhido no portal à esquerda. Sem ícone
 * cadastrado sai só o nome, sem reservar espaço — o portal deixa o ícone
 * opcional e a lista não deve ganhar um recuo vazio por isso.
 */
function SubcategoryLabel({ item, truncate = false }: { item: CategoryItem; truncate?: boolean }) {
    return (
        <span className="flex min-w-0 items-center gap-2">
            {item.icon && <Icon name={item.icon} size={18} className="shrink-0" />}
            <span className={truncate ? "truncate" : undefined}>{item.label}</span>
        </span>
    );
}

/**
 * Card de detalhes de um departamento: título, subcategorias, marcas e banner.
 * Reutilizado nas variantes clássica (dropdown) e moderna (popover do drawer).
 *
 * Subcategorias com filhos próprios (ex.: "Ração" > "Ração Seca"/"Ração Úmida")
 * navegam em drill-down DENTRO da própria coluna via `useViewStack` (mesmo
 * mecanismo do CategoryPanelMobile), com profundidade arbitrária — o nível
 * ativo sai de `resolveCategoryPath`. O chamador deve remontar este componente
 * com `key={category.id}` ao trocar de departamento; sem isso o drill-down de
 * um departamento vazaria visualmente pro próximo.
 */
export function CategoryDetailCard({ category, onClose, className }: CategoryDetailCardProps) {
    const views = useViewStack(category.id);

    // A pilha começa no próprio departamento, então a árvore de busca é [category].
    const level = lastOf(resolveCategoryPath([category], views.stack)) ?? category;
    const children = level.children ?? [];
    // Marcas são sempre as do departamento — não mudam com o drill-down.
    const brands = category.brands ?? [];
    const banner =
        category.panelImageDesktopUrl ?? category.bannerUrl ?? category.imageUrl ?? category.panelImageMobileUrl;
    // Vídeo ganha da arte: o portal grava um ou outro, mas uma arte legada pode sobrar.
    const video = category.panelVideoDesktopUrl ?? category.panelVideoMobileUrl;
    const panelProductIds = category.panelProductIds?.length ? category.panelProductIds : undefined;

    return (
        <div className={`flex flex-1 gap-6 overflow-hidden p-6 ${className ?? ""}`}>
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-4 flex shrink-0 items-center justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-1">
                        {views.canGoBack && (
                            <button
                                type="button"
                                onClick={() => views.back()}
                                aria-label="Voltar"
                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-base-content/60 transition-colors hover:bg-base-200 hover:text-base-content"
                            >
                                <RiArrowLeftSLine size={18} />
                            </button>
                        )}
                        <h3 className="truncate text-lg font-bold text-base-content">{level.label}</h3>
                    </div>
                    <Link
                        href={level.href ?? "#"}
                        onClick={onClose}
                        className="shrink-0 text-sm font-semibold text-primary transition-colors hover:text-primary/70"
                    >
                        Ver todos
                    </Link>
                </div>

                {/* Rola por dentro quando o card tem teto de altura (mega-menu e
                    dropdown das abas, limitados à tela): o título e o "Ver todos"
                    ficam fixos e as subcategorias do fim continuam alcançáveis.
                    Sem teto (popover do drawer), cresce como antes. A rolagem
                    fica num wrapper em BLOCO: se a lista fosse item flex do
                    container rolável, o Chrome balancearia o `columns-2` pela
                    altura visível e abriria colunas extras na horizontal. */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="flex gap-16">
                        {/* Lista corrida em 2 colunas balanceadas, na ordem do backend
                            (sortOrder) — sem agrupar por letra. O espaçamento fica no
                            `li` (e não em `space-y-*`) para o topo das duas colunas
                            ficar alinhado. */}
                        {children.length > 0 && (
                            <ul className="flex-1 columns-2 gap-x-14 [column-fill:balance]">
                                {children.map((sub) => (
                                    <li key={sub.id} className="break-inside-avoid pb-3.5">
                                        {sub.children && sub.children.length > 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => views.push(sub.id)}
                                                className={`flex w-full items-center justify-between gap-1 text-left ${ITEM_CLASS}`}
                                            >
                                                <SubcategoryLabel item={sub} truncate />
                                                <RiArrowRightSLine size={16} className="shrink-0 opacity-60" />
                                            </button>
                                        ) : (
                                            <Link
                                                href={sub.href ?? "#"}
                                                onClick={onClose}
                                                className={`flex items-center ${ITEM_CLASS}`}
                                            >
                                                <SubcategoryLabel item={sub} />
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {brands.length > 0 && (
                            <div className="flex-1">
                                <h4 className="mb-3 text-sm font-semibold text-primary">Marcas mais vendidas</h4>
                                <ul className="flex max-h-90 flex-col gap-3.5 overflow-y-auto">
                                    {brands.map((brand) => (
                                        <li key={brand.id}>
                                            <Link href={brand.href ?? "#"} onClick={onClose} className={ITEM_CLASS}>
                                                {brand.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* A vitrine ocupa o lugar da arte: o portal grava uma ou outra. Sem
                produto vendável na loja, ela some inteira. A coluna tem a largura
                do card (w-52), não a da arte retrato (w-72), para não sobrar vão. */}
            {panelProductIds ? (
                <CategoryPanelProducts
                    productIds={panelProductIds}
                    layout="column"
                    onNavigate={onClose}
                    className="flex min-h-0 w-52 shrink-0 flex-col overflow-y-auto"
                />
            ) : (
                (video || banner) && (
                    <div className="flex min-h-0 w-72 shrink-0 flex-col">
                        {/* `min-h-0`: em tela baixa a arte encolhe (o `object-cover`
                            apara) para o botão abaixo dela não ser cortado. */}
                        <div className="relative aspect-[288/400] max-h-100 min-h-0 w-full overflow-hidden rounded-xl bg-base-200">
                            {video ? (
                                <CategoryPanelVideo path={video} />
                            ) : (
                                <MediaImage
                                    path={banner}
                                    alt={`Promoção ${category.label}`}
                                    fill
                                    sizes="288px"
                                    className="object-cover"
                                />
                            )}
                        </div>
                        <Link href={category.href ?? "#"} onClick={onClose} className="btn btn-primary btn-md mt-2 w-full shrink-0">
                            Visualizar todas
                        </Link>
                    </div>
                )
            )}
        </div>
    );
}
